"use client";

import React, { useState } from "react";
import Image from "next/image";
import EnqueryForm from "@/components/forms/EnqueryForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";

// "Certified AI-Ready Accountant" hexagon badges (bronze / silver / gold).
const BADGES: Record<number, string> = {
  1: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badge_1.webp",
  2: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badge_2.webp",
  3: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/badge_3.webp",
};

type ModulePoint = string | { bold: string; text: string };

type ToolCard = {
  logo: string;
  name: string;
  logoWidth: number;
  logoHeight: number;
  logoClass: string;
  lead: string;
  rest: string;
};

type ModuleCard = {
  icon: string;
  title: string;
  points: ModulePoint[];
};

type Level = {
  id: number;
  labelColor: string;
  labelClass: string;
  subtitle: string;
  type: "tools" | "modules";
  gridCols: string;
  tools?: ToolCard[];
  modules?: ModuleCard[];
};

const levels: Level[] = [
  {
    id: 1,
    labelColor: "#BA8F5A",
    labelClass: "text-[#BA8F5A]",
    subtitle: "AI Mindset, Skillset & Toolset",
    type: "tools",
    gridCols: "lg:grid-cols-4",
    tools: [
      {
        logo: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/copilot_new.webp",
        name: "Microsoft 365 Copilot",
        logoWidth: 70,
        logoHeight: 50,
        logoClass: "w-[70px] h-[50px]",
        lead: "Your daily AI co-pilot",
        rest: " for drafting, summarizing, and explaining. Turns emails, docs, and Excel into first-pass client-ready outputs.",
      },
      {
        logo: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/power_bi._newwebp.webp",
        name: "Power BI",
        logoWidth: 47,
        logoHeight: 50,
        logoClass: "w-[47px] h-[50px]",
        lead: "Your analytics layer",
        rest: " for dashboards and exception detection. Track KPIs, trends, and outliers across the full data set.",
      },
      {
        logo: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/power_automate_new.webp",
        name: "Power Automate",
        logoWidth: 85,
        logoHeight: 25,
        logoClass: "w-[85px] h-[25px]",
        lead: "Your workflow engine",
        rest: " for routing work with controls and audit trail. Automate approvals, reminders, task routing, and evidence filing.",
      },
      {
        logo: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/copilot_studio_new.webp",
        name: "Copilot Studio",
        logoWidth: 76,
        logoHeight: 40,
        logoClass: "w-[76px] h-[40px]",
        lead: "Your agent builder",
        rest: ' for accounting assistants grounded in SOPs. Standardize "what next," guide teams, and trigger workflows end-to-end.',
      },
    ],
  },
  {
    id: 2,
    labelColor: "#B8BCC6",
    labelClass: "text-[#B8BCC6]",
    subtitle: "Applied AI Across Functions",
    type: "modules",
    gridCols: "lg:grid-cols-3",
    modules: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/tax.webp",
        title: "Audit",
        points: [
          "Analytics first; sampling as fallback",
          "Automated PBC intake and tracking",
          "AI-assisted workpapers, faster reviews",
          "Continuous exception monitoring",
        ],
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/taxCheck.webp",
        title: "Tax",
        points: [
          "Prep automates; humans review and advise",
          "Structured intake with completeness checks",
          "AI-drafted notices, approval-led",
          "Advisory signals from patterns",
        ],
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/PFA.webp",
        title: "CAAS / CFO Teams",
        points: [
          "Continuous close replaces fire drills",
          "Controlled approvals with audit trail",
          "Exceptions flagged early, with controls",
          "Dashboards with AI narratives",
        ],
      },
    ],
  },
  {
    id: 3,
    labelColor: "#FFBA08",
    labelClass: "text-[#FFBA08]",
    subtitle: "Firm-Wide AI Operating Model",
    type: "modules",
    gridCols: "lg:grid-cols-4",
    modules: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/operatingModel.webp",
        title: "Operating Model",
        points: [
          "Shift to exception-led delivery with clear human review points",
          "Define who drafts, who reviews, who approves in an AI-assisted world",
          "Separate production, judgment, and accountability clearly",
        ],
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Agents.webp",
        title: "Agents at Scale",
        points: [
          "Deploy role-specific agents (Close Assistant, Audit Prep Agent, Tax Ops Agent)",
          "Agents guide “what next,” enforce standards, and trigger workflows",
          "Knowledge moves from people to systems, surviving turnover & busy-season churn",
        ],
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Standardization.webp",
        title: "Standardization",
        points: [
          "Firm-wide prompt libraries, SOPs, and templates as system inputs",
          "Consistent workflows for audit, tax, close, and FP&A",
          "Reduced variation, higher quality, faster onboarding",
        ],
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/caira/new/Governance.webp",
        title: "Governance & Trust",
        points: [
          "Role-based access and approved data sources",
          "Human-in-the-loop for final outputs - your team owns the sign-offs",
          "Audit trail across workflows, approvals, and decisions",
        ],
      },
    ],
  },
];

const HexBadge = ({ color }: { color: string }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <polygon points="24,2 44,13 44,35 24,46 4,35 4,13" fill={color} />
    <polygon
      points="24,9 38,16.75 38,31.25 24,39 10,31.25 10,16.75"
      fill="none"
      stroke="#04101C"
      strokeOpacity="0.35"
      strokeWidth="1.5"
    />
    <text
      x="24"
      y="27"
      textAnchor="middle"
      fontSize="9"
      fontWeight="700"
      fill="#04101C"
    >
      CAIRA
    </text>
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    className={`shrink-0 transition-transform duration-300 ${
      open ? "rotate-180" : ""
    }`}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AboutCaira = ({ hideForms = false }: { hideForms?: boolean }) => {
  const [openLevel, setOpenLevel] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleEnrollNow = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <section className="w-full bg-black bg-cover bg-center bg-no-repeat py-7 md:py-[50px] md:bg-[url('https://ik.imagekit.io/mileseducation/miles_website/caira/new/about_caira.webp')] md:bg-top md:bg-[length:100%_auto]">
      <div className="main_container mx-auto px-4 md:px-6 md:max-w-[1280px]">
        {/* Title */}
        <div className="text-center mb-4 md:mb-8">
          <h2 className="text-[22px] md:text-[30px] font-bold text-[#FFBA08] leading-tight">
            More About CAIRA
          </h2>
          <p className=" text-white text-sm md:text-base font-semibold mt-1">
            3 levels | 90 Hours | NASBA-approved CPE
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-4">
          {levels.map((level) => {
            const isOpen = openLevel === level.id;
            return (
              <div
                key={level.id}
                className="rounded-[14px] bg-[#202020] overflow-hidden"
              >
                {/* Header */}
                <button
                  type="button"
                  onClick={() => setOpenLevel(isOpen ? 0 : level.id)}
                  className="w-full flex items-center justify-between gap-3 p-[10px] md:p-5 text-left"
                  aria-expanded={isOpen ? "true" : "false"}
                >
                  <div className="flex items-center gap-2 md:gap-2">
                    {BADGES[level.id] ? (
                      <Image
                        src={BADGES[level.id]}
                        alt={`CAIRA Level ${level.id} badge`}
                        width={56}
                        height={56}
                        className="shrink-0 w-11 h-11 md:w-14 md:h-14 object-contain"
                      />
                    ) : (
                      <HexBadge color={level.labelColor} />
                    )}
                    <div>
                      <p className="flex items-center gap-1.5 text-[14px] md:text-[16px] font-bold leading-tight">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/caira/caira_sm_logo.webp"
                          alt="CAIRA"
                          width={110}
                          height={17}
                          className="w-[40px] md:w-[48px] h-auto object-contain"
                        />
                        <span className={level.labelClass}>
                          Level {level.id}
                        </span>
                      </p>
                      <p className="text-white text-xs md:text-sm mt-0.5">
                        {level.subtitle}
                      </p>
                    </div>
                  </div>
                  <Chevron open={isOpen} />
                </button>

                {/* Content */}
                {isOpen && (
                  <div className="px-[10px] md:px-5 pb-4 md:pb-5">
                    <div
                      className={`grid grid-cols-1 sm:grid-cols-2 ${level.gridCols} gap-3 md:gap-4 max-md:flex max-md:overflow-x-auto max-md:snap-x max-md:[scrollbar-width:none]`}
                    >
                      {level.type === "tools"
                        ? level.tools!.map((tool) => (
                            <div
                              key={tool.name}
                              className="rounded-[10px] border border-[rgba(255,255,255,0.06)] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_0_rgba(166,137,255,0.30)] backdrop-blur-[4.92px] p-[10px] flex flex-col max-md:shrink-0 max-md:w-[240px] max-md:snap-start"
                            >
                              <div className="flex items-center justify-center min-h-[67px]">
                                <Image
                                  src={tool.logo}
                                  alt={tool.name}
                                  width={tool.logoWidth}
                                  height={tool.logoHeight}
                                  className={`object-contain ${tool.logoClass}`}
                                />
                              </div>
                              <p className="text-xs md:text-sm leading-[1.5] mt-3">
                                <span className="text-[#3399FF] font-bold">
                                  {tool.lead}
                                </span>
                                <span className="text-white font-light">
                                  {tool.rest}
                                </span>
                              </p>
                            </div>
                          ))
                        : level.modules!.map((mod) => (
                            <div
                              key={mod.title}
                              className="rounded-[10px] border border-[rgba(255,255,255,0.06)] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_0_rgba(166,137,255,0.30)] backdrop-blur-[4.92px] p-4 flex flex-col max-md:shrink-0 max-md:w-[260px] max-md:snap-start"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Image
                                  src={mod.icon}
                                  alt={mod.title}
                                  width={32}
                                  height={32}
                                  className="w-4 h-4 object-contain"
                                />
                                <span className="text-white text-xs md:text-sm font-bold">
                                  {mod.title}
                                </span>
                              </div>
                              <ul className="space-y-1.5">
                                {mod.points.map((pt, i) => (
                                  <li
                                    key={i}
                                    className="flex gap-2 text-xs md:text-sm leading-[1.45] text-white font-light"
                                  >
                                    <span className="text-white shrink-0 md:text-base">
                                      •
                                    </span>
                                    <span>
                                      {typeof pt === "string" ? (
                                        pt
                                      ) : (
                                        <>
                                          <span className="font-semibold">
                                            {pt.bold}
                                          </span>{" "}
                                          {pt.text}
                                        </>
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {!hideForms && (
          <div className="flex justify-center mt-6 md:mt-8">
            <button
              type="button"
              onClick={handleEnrollNow}
              className="inline-flex items-center justify-center rounded-[8px] bg-[#3399FF] hover:bg-[#2b86e2] transition-colors text-white font-bold text-sm md:text-sm px-[8px] py-[9px] text-center"
            >
              Start Level 1: AI-Readiness for Accountants
            </button>
          </div>
        )}
      </div>

      {!hideForms && isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999] px-4">
          <div
            className="relative p-6 w-full md:w-[430px] rounded-2xl border"
            style={{
              background: "rgba(33, 100, 167, 0.10)",
              boxShadow: "0 0 54.9px 0 rgba(166, 137, 255, 0.30) inset",
              borderColor: "rgba(255, 255, 255, 0.06)",
              backdropFilter: "blur(8px)",
            }}
          >
            <button
              type="button"
              onClick={closeForm}
              className="absolute right-2 top-0 text-white px-3 py-1 rounded"
            >
              ✕
            </button>
            <EnqueryForm
              course="CAIRA"
              courseId={1}
              formHeading={FormHeading.TALK_TO_CAIRA_ADVISOR}
              formType="page"
              isAStepperForm
              subCategory={FinanceAndAccountingSubcategory.CPA}
              submitButtonName="Enroll Now"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default AboutCaira;
